import Typography from '@mui/material/Typography';
import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import type { TranslationTag } from '@ps/web-core-intl';
import { StarsButton, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import useRequest from '@swf/core/hooks/useRequest';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import type { ApplePayMerchantCapability } from '../../../../../../types/Cashier';
import Prompt from '../../../../../Prompt/Prompt';

import {
  TXTWCL_Okay,
  TXTWCL_Please_ensure_that_your_Apple__ELL,
  TXTWCL_Transaction_Declined,
  TXTWCL_Your_Apple_Pay_deposit_has_bee_ELL
} from './ApplePay.messages.json';
import { button, process } from './ApplePay.scss';

interface ApplePayResult {
  exception?: boolean;
  canceled?: boolean;
  paymentToken?: string;
}

const passkitToWebMapping: Record<string, ApplePayMerchantCapability> = {
  capability3DS: 'supports3DS',
  capabilityEMV: 'supportsEMV',
  capabilityCredit: 'supportsCredit',
  capabilityDebit: 'supportsDebit'
};

const ApplePay: React.FC = () => {
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<TranslationTag | null>(null);
  const countryCode = useSelector(select.account.getCountry) as string;
  const hostname = useSelector(select.location.getHostname);
  const embedded = useSelector(select.device.isEmbedded);
  const brand = useSelector(select.cms.getBrandId);
  const {
    data: {
      paySystemDetails: {
        info: { accountId, supportedNetworks, merchantCapabilities }
      }
    }
  } = useWizardContext();

  const {
    input: {
      value: { amount, currency }
    }
  } = useField('deposit', { subscription: { value: true } });
  const request = useRequest();
  const form = useForm();

  const onApplePayCompleted = useCallback(
    (applePayResult: ApplePayResult) => {
      setProcessing(false);
      if (applePayResult.paymentToken) {
        form.change('deposit.applePayPaymentToken', applePayResult.paymentToken);
        form.submit();
      }
      if (applePayResult.exception) {
        setErrorMessage(TXTWCL_Please_ensure_that_your_Apple__ELL);
      }
      if (applePayResult.canceled) {
        setErrorMessage(TXTWCL_Your_Apple_Pay_deposit_has_bee_ELL);
      }
    },
    [form]
  );

  const cancelPaymentSession = (session: ApplePaySession) => {
    if (session) {
      session.abort();
    }

    onApplePayCompleted({ canceled: true });
  };

  const initApplePaySession = (paymentRequest: ApplePayJS.ApplePayPaymentRequest) => {
    try {
      const session = new ApplePaySession(1, paymentRequest);
      session.onvalidatemerchant = ({ validationURL }) => {
        request({
          service: 'wc3.cards.validateApplePayMerchant',
          body: { url: validationURL, initiativeContext: hostname }
        }).subscribe(
          ({ body }) => {
            if (
              typeof body === 'string' ||
              'statusCode' in body ||
              !(
                'merchantIdentifier' in body &&
                'merchantSessionIdentifier' in body &&
                ('nOnce' in body || 'nonce' in body)
              )
            ) {
              cancelPaymentSession(session);
              return;
            }

            session.completeMerchantValidation(body);
          },
          () => cancelPaymentSession(session)
        );
      };
      session.onpaymentauthorized = ({ payment }) => {
        const paymentToken = JSON.stringify(payment.token);
        try {
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          onApplePayCompleted({ paymentToken });
        } catch {
          onApplePayCompleted({ exception: true });
        }
      };
      session.oncancel = () => {
        onApplePayCompleted({});
      };
      session.begin();
    } catch {
      onApplePayCompleted({ exception: true });
    }
  };

  const handleApplePayButtonClick = () => {
    const { valid } = form.getState();
    if (!amount || !valid) {
      form.submit();
      return;
    }
    setProcessing(true);

    const baseRequest = {
      countryCode,
      currencyCode: currency,
      supportedNetworks:
        supportedNetworks && supportedNetworks.length ? supportedNetworks : ['visa', 'masterCard']
    };

    const newMerchantCapabilities: ApplePayMerchantCapability[] =
      merchantCapabilities && merchantCapabilities.length
        ? merchantCapabilities.map((e) => passkitToWebMapping[e])
        : ['supports3DS'];

    const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
      ...baseRequest,
      total: {
        label: BrandTypes[brand],
        amount
      },
      merchantCapabilities: newMerchantCapabilities
    };
    if (embedded) {
      const nativePaymentRequest = {
        ...baseRequest,
        merchantId: accountId,
        total: amount.toString(),
        title: BrandTypes[brand],
        merchantCapabilities:
          merchantCapabilities && merchantCapabilities.length
            ? merchantCapabilities
            : ['capability3DS']
      };

      window.starsweb?.applepay?.requestPayment?.(
        Buffer.from(JSON.stringify(nativePaymentRequest)).toString('base64')
      );
    } else {
      initApplePaySession(paymentRequest);
    }
  };

  const onApplePayNativeResponse = useCallback(
    (event: CustomEvent) => {
      if (event.detail.payload) {
        onApplePayCompleted({
          paymentToken: Buffer.from(event.detail.payload, 'base64').toString()
        });
      } else {
        onApplePayCompleted({});
      }
    },
    [onApplePayCompleted]
  );

  useEffect(() => {
    if (embedded) {
      document.addEventListener(
        'applePayPaymentResponse',
        onApplePayNativeResponse as EventListenerOrEventListenerObject
      );
    }
    return () =>
      document.removeEventListener(
        'applePayPaymentResponse',
        onApplePayNativeResponse as EventListenerOrEventListenerObject
      );
  }, [embedded, onApplePayNativeResponse]);

  return (
    <>
      {errorMessage && (
        <Prompt
          variant="info"
          title={{ translation: TXTWCL_Transaction_Declined }}
          primaryButton={{ translation: TXTWCL_Okay }}
          onPrimaryButtonClick={() => setErrorMessage(null)}
          onClose={() => setErrorMessage(null)}
        >
          <Typography>
            <Translation {...errorMessage} />
          </Typography>
        </Prompt>
      )}
      <StarsButton
        data-testid="apple-button"
        disabled={processing}
        className={classNames(button, { [process]: processing })}
        onClick={handleApplePayButtonClick}
      />
    </>
  );
};

export default ApplePay;
